import { z } from 'zod';
import { StepInputEventSchema, StepInputPreviousOutputSchema, StepInputSchema } from '../../shared/step-input.schema';
import { StepActionTypeSchema } from '../../shared/step-action-type.schema';
import { EntitySuggestionSchema, EntityOrSuggestionRefSchema } from '../entity-suggestion';
import { PopulatedProcessSchema } from '../process/process.schema';
import { SurveySchema } from '../survey/survey.schema';
import { PopulatedUserSchema, UserDisplayIdentitySchema } from '../user/user.schema';
import { ArtifactSchema } from '../artifact/artifact.schema';
import { PopulatedExternalContactSchema } from '../external-contact/external-contact.schema';
import { AssigneeModelSchema } from '../../shared/assignee-model.schema';
import { FrequencyValueSchema } from '../../shared/capacity';
// Re-export shared input schemas for backward compatibility
export { StepInputEventSchema, StepInputPreviousOutputSchema, StepInputSchema };
/**
 * Recording Status Enum
 *
 * Authoritative lifecycle state of a ProcessStepRecording. `'finalized'` is
 * the only signal callers should use to detect completion — never branch on
 * `recordingCompletedAt` as a boolean. The two fields are paired on the
 * finalize transition and enforced via a pre-save invariant in the backend
 * Mongoose schema.
 *
 * - in_progress: user is still editing the recording
 * - pending_finalization: user stopped recording, awaiting AI summary review
 * - finalized: recording is locked in and counted as complete
 */ export const RecordingStatusSchema = z.enum([
    'in_progress',
    'pending_finalization',
    'finalized'
]);
/**
 * Populated orgFunction/system refs intentionally use the same `{ type, id }` shape
 * as the unpopulated form — backend doesn't expand them server-side. Consumers
 * (frontend cards, admin lists) look up `id` against locally-fetched hashes:
 * `orgFunctionHashById` / `systemHashById` for entity refs, and the recording's own
 * `entitySuggestions[]` populated array for suggestion refs.
 *
 * This keeps the wire shape compact (no duplicated orgFunction/system payloads when
 * the same orgFunction appears across many slots) at the cost of one client-side
 * lookup per render — which the picker hooks already do.
 */ /**
 * Step Successors Schema
 * Defines who/what receives the output of a process step.
 *
 * `orgFunctions` and `systems` carry an EntityOrSuggestionRef (a real entity ref
 * or a pending/rejected suggestion ref). A `orgFunctions` entry resolves into the
 * `orgFunctions` collection and may be of either kind (team or role). `persons` and
 * `externalContacts` stay as plain string IDs — those entity classes are created
 * directly without an approval workflow.
 */ export const StepSuccessorsSchema = z.object({
    persons: z.array(z.string()).optional(),
    orgFunctions: z.array(EntityOrSuggestionRefSchema).optional(),
    systems: z.array(EntityOrSuggestionRefSchema).optional(),
    externalContacts: z.array(z.string()).optional(),
    successorStatus: z.enum([
        'UNCLEAR',
        'NONE',
        ''
    ]).optional()
});
/**
 * Step Helpers Schema
 * Resources used during step execution (unpopulated). `systems` and
 * `orgFunctions` carry union refs.
 */ export const StepHelpersSchema = z.object({
    persons: z.array(z.string()).optional(),
    /** Functions (teams/roles) that help with the step. Parallel to `persons`, per the StepSuccessors shape. */ orgFunctions: z.array(EntityOrSuggestionRefSchema).optional(),
    systems: z.array(EntityOrSuggestionRefSchema).optional(),
    documents: z.array(z.string()).optional()
});
/**
 * Populated Step Helpers Schema
 * Resources used during step execution. `systems` and `orgFunctions` carry the
 * unpopulated union ref (see note above); persons/documents are populated as before.
 */ export const PopulatedStepHelpersSchema = z.object({
    persons: z.array(PopulatedUserSchema).optional(),
    /** Functions (teams/roles) that help with the step. Parallel to `persons`, per the StepSuccessors shape. */ orgFunctions: z.array(EntityOrSuggestionRefSchema).optional(),
    systems: z.array(EntityOrSuggestionRefSchema).optional(),
    documents: z.array(ArtifactSchema).optional()
});
/**
 * Populated Step Successors Schema
 * `orgFunctions` and `systems` carry the unpopulated union ref (see note above);
 * other slots are populated as before.
 */ export const PopulatedStepSuccessorsSchema = z.object({
    persons: z.array(PopulatedUserSchema).optional(),
    orgFunctions: z.array(EntityOrSuggestionRefSchema).optional(),
    systems: z.array(EntityOrSuggestionRefSchema).optional(),
    externalContacts: z.array(PopulatedExternalContactSchema).optional(),
    successorStatus: z.enum([
        'UNCLEAR',
        'NONE',
        ''
    ]).optional()
});
/**
 * Decision Rule Input Schema
 * One condition cell of a decision table: the `column` is the user-entered
 * column name and `value` is this rule's expected value for that column.
 * Stored as an ordered array (not a `column -> value` map) so column names may
 * contain any character — Mongo field names / Mongoose Map keys forbid `.`,
 * which broke publishing for column names like "BANF i.O.?".
 */ export const StepDecisionRuleInputSchema = z.object({
    column: z.string(),
    value: z.string()
});
/**
 * Decision Rule Schema
 * Defines conditional logic for decision-type steps (unpopulated - contains union refs only)
 */ export const StepDecisionRuleSchema = z.object({
    _id: z.string().optional(),
    inputs: z.array(StepDecisionRuleInputSchema),
    output: z.string(),
    successors: StepSuccessorsSchema
});
/**
 * Populated Decision Rule Schema
 * Defines conditional logic for decision-type steps (populated with full entity / suggestion data)
 */ export const PopulatedStepDecisionRuleSchema = z.object({
    _id: z.string().optional(),
    inputs: z.array(StepDecisionRuleInputSchema),
    output: z.string(),
    successors: PopulatedStepSuccessorsSchema
});
/**
 * Who Else Does This Status Enum
 * Represents the status of a person who also performs this step
 */ export const WhoElseDoesThisStatusSchema = z.enum([
    'pending',
    'accepted',
    'declined'
]);
/**
 * Who Else Does This Model Enum
 * Defines the type of entity in whoElseDoesThis (User or OrgFunction). A OrgFunction
 * entry resolves into the `orgFunctions` collection and may be of either kind.
 */ export const WhoElseDoesThisModelSchema = z.enum([
    'User',
    'OrgFunction'
]);
/**
 * Who Else Does This Item Schema
 * Represents an entity (User or OrgFunction) who also performs this step with their acceptance status.
 *
 * `entity` is always an EntityOrSuggestionRef regardless of `model`. Users never go
 * through suggestions, so a User entry is always `{ type: 'entity', id }` — but the
 * wrapper stays uniform so consumers branch on `entity.type` identically across models.
 */ export const WhoElseDoesThisItemSchema = z.object({
    entity: EntityOrSuggestionRefSchema,
    model: WhoElseDoesThisModelSchema,
    status: WhoElseDoesThisStatusSchema
});
/**
 * Who Else Does This Item Populated Schema
 *
 * `entity` stays as the unpopulated union ref (see populated-ref note above);
 * the resolved user or orgFunction payload is looked up client-side via the
 * recording's own `entitySuggestions[]` (for suggestion entries) or the
 * caller's user/orgFunction hash (for entity entries).
 */ export const WhoElseDoesThisItemPopulatedSchema = z.object({
    entity: EntityOrSuggestionRefSchema,
    model: WhoElseDoesThisModelSchema,
    status: WhoElseDoesThisStatusSchema
});
/**
 * Assignee ref schema (top-level `assignedTo` on a ProcessStepRecording).
 *
 * Uniform `EntityOrSuggestionRef` regardless of `assignedToModel`. Only OrgFunction
 * assignees can be suggestion refs; User/System/ExternalContact entries are
 * always `{ type: 'entity', id }`. The wrapper stays uniform so consumers
 * branch on `.type` identically across assignee models.
 */ export const AssignedToRefSchema = EntityOrSuggestionRefSchema;
/**
 * Populated assignee ref: identical to unpopulated `AssignedToRef`. See the
 * populated-ref note above for the rationale.
 */ export const PopulatedAssignedToRefSchema = EntityOrSuggestionRefSchema;
/**
 * Populated Process Step Recording Schema
 * Used when process step recording references are populated with simplified data
 * Simplified version for populated references in API responses (e.g., in process-step-assignment)
 */ export const PopulatedProcessStepRecordingSchema = z.object({
    /**
   * Node-kind discriminator (ADR 0003): steps and events share one collection,
   * so a `previousStep` ref can resolve to an event node — but this populated
   * arm is step-only; the backend population guard depopulates event nodes
   * back to plain id strings (a slot only receives populated event payloads
   * when one of its union arms declares an `'event'` nodeKind literal).
   * Defaults to `'step'` so legacy payloads without the field still parse.
   */ nodeKind: z.literal('step').default('step'),
    _id: z.string(),
    actionType: StepActionTypeSchema,
    inputs: z.array(z.union([
        StepInputEventSchema,
        StepInputPreviousOutputSchema
    ])).optional(),
    task: z.object({
        taskDescription: z.string(),
        output: z.string()
    }).optional(),
    rules: z.array(StepDecisionRuleSchema).optional(),
    generatedName: z.string().nullable().optional(),
    generatedDescription: z.string().nullable().optional(),
    recordedBy: z.union([
        z.string(),
        PopulatedUserSchema
    ]).optional(),
    assignedTo: PopulatedAssignedToRefSchema.nullable().optional(),
    assignedToModel: AssigneeModelSchema.optional(),
    status: RecordingStatusSchema.optional()
});
/**
 * Fields dropped from PopulatedProcessStepRecordingSchema when projecting it onto
 * the survey task-assignment shape. `inputs`, `assignedTo`, and `assignedToModel`
 * are dropped entirely; `status` is dropped here so the derived schema re-adds it
 * with the shape it needs (SurveyStepRecordingSchema keeps the recording status).
 */ export const TASK_ASSIGNMENT_OMITTED_RECORDING_FIELDS = {
    inputs: true,
    assignedTo: true,
    assignedToModel: true,
    status: true
};
/**
 * Survey Step Recording Schema
 *
 * Inherits the canonical populated-recording shape from PopulatedProcessStepRecordingSchema,
 * omitting fields the survey path doesn't populate, and overriding `process` with a union that
 * may be a string ID or fully populated process.
 *
 * Lives here (rather than under `survey/`) so the recording entity does not need to depend on
 * the survey entity at all — breaking the historical recording ↔ survey schema cycle.
 */ export const SurveyStepRecordingSchema = PopulatedProcessStepRecordingSchema.omit(TASK_ASSIGNMENT_OMITTED_RECORDING_FIELDS).extend({
    // z.lazy defers the binding read past the recording ↔ process module cycle:
    // process.schema imports this module, so PopulatedProcessSchema may not be
    // initialized yet when this module body runs (TDZ in the ESM build).
    process: z.union([
        z.string(),
        z.lazy(()=>PopulatedProcessSchema)
    ])
});
/**
 * The primitives a manually entered capacity value carries — the same three a
 * step survey collects, so the calculator can treat the entry as one synthetic
 * survey answer. Validation is deliberately stricter than the survey's own
 * schema: the frequency enum rather than a free string, and integer minutes.
 *
 * The cross-field rule (`frequencyTimesPerPeriod` required exactly for the
 * "mehrmals" frequencies) lives on the write feature's request schema — entity
 * schemas stay refinement-free (bliksy resolveSubSchema constraint).
 */ export const ManualCapacityDisplaySchema = z.object({
    frequency: FrequencyValueSchema,
    frequencyTimesPerPeriod: z.number().int().positive().optional(),
    durationMinutes: z.number().int().positive()
});
/**
 * A step's manually entered capacity, with the attribution the editor dialog
 * shows. Presence is the mark (the AiProvenance idiom): which source is
 * EFFECTIVE is decided per read from the tenant's `capacityPriority`, never
 * stored here.
 *
 * The display schema above drops the attribution on purpose: the population
 * pass is Zod-shape-driven, so a declared `enteredBy` would be auto-populated
 * on a route that never joins the user and fail the parse.
 */ export const ManualCapacitySchema = ManualCapacityDisplaySchema.extend({
    /**
   * Populated with exactly `_id firstName lastName` (draft-populate) — not the
   * full PopulatedUserSchema, which would fail the parse on the missing fields.
   * `null` once the entering user is hard-deleted and the ref dangles.
   */ enteredBy: z.union([
        z.string(),
        z.object({
            _id: z.string(),
            firstName: z.string(),
            lastName: z.string()
        })
    ]).nullable(),
    enteredAt: z.string()
});
/**
 * Process Step Recording Schema (Edit/Minimal Population)
 * Used for edit endpoints - returns union refs for helpers, task.successors, and rules[].successors
 * suitable for direct form binding.
 *
 * Carries no `surveys`: the routes serializing through this schema (show/edit a
 * single recording) are open to every recorder, while survey content is
 * manager-only. Only the display schema below has the field, and only
 * show-process fills it — behind the sensitive-fields gate. Zod strips unknown
 * keys, so leaving the field out drops surveys on the parse.
 *
 * The parse is not the only reason nothing leaks: neither service joins surveys
 * at all (pinned in their specs), so the `populatedDoc.toObject()` fallback both
 * routes take on a validation failure has nothing to carry either. Documenting
 * that rather than hardening it was the deliberate ruling — the guard is owed by
 * the first caller that starts joining surveys onto these routes, not before.
 */ export const ProcessStepRecordingSchema = z.object({
    /**
   * Node-kind discriminator (ADR 0003): steps and events share one
   * collection. Defaults to 'step' so legacy payloads without the field
   * still parse. Must stay refine-free (bliksy resolveSubSchema constraint).
   */ nodeKind: z.literal('step').default('step'),
    _id: z.string(),
    process: z.string(),
    previousStep: z.union([
        z.string(),
        z.null(),
        PopulatedProcessStepRecordingSchema
    ]),
    stepNumber: z.number().optional(),
    // Nullable: a hard-deleted user (delete-tenant-user) leaves a dangling ref
    // and `populate()` resolves it to null. Consumers must handle the absence.
    recordedBy: PopulatedUserSchema.nullable(),
    assignedTo: AssignedToRefSchema.optional(),
    assignedToModel: AssigneeModelSchema.optional(),
    whoElseDoesThis: z.array(WhoElseDoesThisItemSchema),
    inputs: z.array(StepInputSchema),
    actionType: StepActionTypeSchema,
    helpers: StepHelpersSchema.optional(),
    // Authoritative only when actionType === 'task'; kept across a toggle, so it
    // may coexist with `rules`.
    task: z.object({
        taskDescription: z.string().optional(),
        output: z.string().optional(),
        successors: StepSuccessorsSchema.optional()
    }).optional(),
    // Authoritative only when actionType === 'decision'; kept across a toggle, so
    // it may coexist with `task`.
    rules: z.array(StepDecisionRuleSchema).optional(),
    entitySuggestions: z.array(EntitySuggestionSchema),
    /**
   * Timestamp of the finalize transition. Set if and only if
   * `status === 'finalized'`. Used for due-date math; never branched on as a
   * boolean — use `status` for that.
   */ recordingCompletedAt: z.number().optional(),
    status: RecordingStatusSchema,
    generatedName: z.string().nullable().optional(),
    generatedDescription: z.string().nullable().optional(),
    /**
   * Nullable as well as optional: the DELETE endpoint answers with the step
   * node carrying an explicit `null`, which is what tells the client cache to
   * clear a value rather than keep the one it holds.
   */ manualCapacity: ManualCapacitySchema.nullable().optional(),
    createdAt: z.string(),
    updatedAt: z.string(),
    deletedAt: z.string().nullable().optional()
});
/**
 * One description behind the `different` count, attributed to its author (ADR
 * 0019 amendment): a manager reading how the work really goes needs to know
 * whom to ask about it. `author: null` is a hard-deleted or dangling user ref —
 * a description is never dropped because its author could not be resolved.
 * `deletedAt` (epoch ms) marks a soft-deleted author, whose name and avatar are
 * still shown with the tombstone suffix.
 */ export const StepVariantDescriptionSchema = z.object({
    text: z.string(),
    author: UserDisplayIdentitySchema.extend({
        deletedAt: z.number().optional()
    }).nullable()
});
/**
 * Variant Check block for one step: what its co-performers answered, aggregated.
 *
 * The COUNTS are anonymous — `same` and `notPerformed` carry no identity at all.
 * The `different` descriptions are attributed (ADR 0019 amendment supersedes the
 * blanket anonymity of ADR 0022 for the free text), in the order they were
 * answered. Accepted consequence: naming an author reveals that they answered
 * "different".
 *
 * `variants` is optional so a surface that withholds the free text — the
 * process export, the analyzer projection, both still anonymous — can OMIT the
 * key rather than send `[]`, which a reader cannot tell from "nobody wrote
 * anything". It is named `variants` rather than reusing the pre-attribution
 * `descriptions` (a string array) so a stale bundle still open across the
 * deploy renders counts only instead of crashing on an object child.
 */ export const StepVariantCheckSchema = z.object({
    same: z.number().int().nonnegative(),
    different: z.number().int().nonnegative(),
    notPerformed: z.number().int().nonnegative(),
    variants: z.array(StepVariantDescriptionSchema).optional()
});
/**
 * Process Step Recording Display Schema (Full Population)
 * Used for display endpoints - returns populated wrappers for helpers, task.successors, and rules[].successors
 * suitable for direct rendering in UI.
 */ export const ProcessStepRecordingDisplaySchema = z.object({
    /**
   * Node-kind discriminator (ADR 0003); see ProcessStepRecordingSchema.
   * Event nodes never travel through this schema — they get their own
   * display schema and population pass.
   */ nodeKind: z.literal('step').default('step'),
    _id: z.string(),
    process: z.string(),
    previousStep: z.union([
        z.string(),
        z.null(),
        PopulatedProcessStepRecordingSchema
    ]),
    stepNumber: z.number().optional(),
    // Nullable: a hard-deleted user (delete-tenant-user) leaves a dangling ref
    // and `populate()` resolves it to null. Consumers must handle the absence.
    recordedBy: PopulatedUserSchema.nullable(),
    assignedTo: PopulatedAssignedToRefSchema.optional(),
    assignedToModel: AssigneeModelSchema.optional(),
    whoElseDoesThis: z.array(WhoElseDoesThisItemPopulatedSchema),
    inputs: z.array(StepInputSchema),
    actionType: StepActionTypeSchema,
    helpers: PopulatedStepHelpersSchema.optional(),
    // Authoritative only when actionType === 'task'; kept across a toggle, so it
    // may coexist with `rules`.
    task: z.object({
        taskDescription: z.string().optional(),
        output: z.string().optional(),
        successors: PopulatedStepSuccessorsSchema.optional()
    }).optional(),
    // Authoritative only when actionType === 'decision'; kept across a toggle, so
    // it may coexist with `task`.
    rules: z.array(PopulatedStepDecisionRuleSchema).optional(),
    entitySuggestions: z.array(EntitySuggestionSchema),
    /**
   * Timestamp of the finalize transition. Set if and only if
   * `status === 'finalized'`. Used for due-date math; never branched on as a
   * boolean — use `status` for that.
   */ recordingCompletedAt: z.number().optional(),
    status: RecordingStatusSchema,
    generatedName: z.string().nullable().optional(),
    generatedDescription: z.string().nullable().optional(),
    /**
   * The respondent is projected out on manager reads (ADR 0021 decision 4), so
   * `user` is optional HERE and required everywhere else: anonymity is a
   * property of this wire, not a rendering convention. The respondent-own
   * routes keep the identity, because it is theirs. `_id` drops with it: the
   * recording dashboard serves the same id alongside the respondent's name, so
   * a retained id re-attributes the answers by a join.
   */ surveys: z.array(SurveySchema.partial({
        user: true,
        _id: true
    })).optional(),
    /**
   * Aggregated Variant Check answers, filled by show-process behind the same
   * sensitive-fields gate as `surveys`. Absent when the step has none.
   */ variantCheck: StepVariantCheckSchema.optional(),
    /**
   * Draft-only swimlane placement hint forwarded from `ProcessDraftStep`.
   * Empty for published recordings — only the draft adapter sets it.
   */ laneKey: z.string().optional(),
    /** Primitives only; see {@link ManualCapacityDisplaySchema}. */ manualCapacity: ManualCapacityDisplaySchema.optional(),
    createdAt: z.string(),
    updatedAt: z.string(),
    deletedAt: z.string().nullable().optional()
});

//# sourceMappingURL=process-step-recording.schema.js.map